import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider, useLanguage } from "@/contexts/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Sidebar from "@/components/Sidebar";
import Dashboard from "@/pages/Dashboard";
import Chantiers from "@/pages/Chantiers";
import Materiaux from "@/pages/Materiaux";
import Calendrier from "@/pages/Calendrier";
import Assistant from "@/pages/Assistant";
import Login from "@/pages/Login";
import RDV from "@/pages/RDV";
import DemandesMateriaux from "@/pages/DemandesMateriaux";
import Equipe from "@/pages/Equipe";
import NotesChantier from "@/pages/NotesChantier";
import GaleriePublique from "@/pages/GaleriePublique";
import VisitesChantier from "@/pages/VisitesChantier";
import VisiteCanvas from "@/pages/VisiteCanvas";
import { Plus, Building2, Package, Camera, X, Sparkles, LayoutDashboard, CalendarClock, ClipboardList, MoreHorizontal, Calendar, Users, LogOut, FileText, ClipboardCheck } from "lucide-react";

function ProtectedRoute({ children, patronOnly = false }) {
  const { user, loading, isPatron } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#EEF2F7] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#F97316] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (patronOnly && !isPatron()) return <Navigate to="/" replace />;
  return children;
}

// =========== FAB QUICK ACTIONS ===========
function QuickActionFAB() {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();
  const { isPatron } = useAuth();
  const navigate = useNavigate();

  const patronActions = [
    { icon: Building2, label: t('newChantierAction'), color: '#3B82F6', bg: '#EFF6FF', action: () => { navigate('/chantiers'); setOpen(false); } },
    { icon: FileText, label: t('addNoteChantier'), color: '#F97316', bg: '#FFF7ED', action: () => { navigate('/notes'); setOpen(false); } },
    { icon: Package, label: t('newRequestAction'), color: '#8B5CF6', bg: '#F5F3FF', action: () => { navigate('/demandes'); setOpen(false); } },
    { icon: Sparkles, label: t('assistant'), color: '#10B981', bg: '#ECFDF5', action: () => { navigate('/assistant'); setOpen(false); } },
  ];

  const workerActions = [
    { icon: Package, label: t('newRequestAction'), color: '#F97316', bg: '#FFF7ED', action: () => { navigate('/demandes'); setOpen(false); } },
    { icon: Camera, label: t('addPhotoAction'), color: '#3B82F6', bg: '#EFF6FF', action: () => { navigate('/chantiers'); setOpen(false); } },
  ];

  const actions = isPatron() ? patronActions : workerActions;

  return (
    <div className="fab-container">
      {/* Menu items */}
      {open && (
        <div className="fab-menu">
          {actions.map((item, i) => (
            <div
              key={i}
              className="fab-item animate-fadeIn"
              style={{ animationDelay: `${i * 0.05}s` }}
              onClick={item.action}
            >
              <span className="fab-item-label">{item.label}</span>
              <button
                className="fab-item-btn"
                style={{ background: item.bg }}
              >
                <item.icon className="w-5 h-5" style={{ color: item.color }} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Main FAB button */}
      <button
        onClick={() => setOpen(!open)}
        className={`fab-btn ${open ? 'open' : ''}`}
        aria-label={t('quickActions')}
        data-testid="fab-btn"
      >
        {open
          ? <X className="w-6 h-6 transition-transform rotate-0" />
          : <Plus className="w-6 h-6 transition-transform" />
        }
      </button>
    </div>
  );
}

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const { user, isPatron, isOuvrier } = useAuth();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) setSidebarOpen(false);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-[#EEF2F7]">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} isMobile={isMobile} />
      <main className={`flex-1 overflow-y-auto transition-all duration-300 ${!isMobile && sidebarOpen ? 'ml-64' : !isMobile ? 'ml-20' : 'ml-0'}`}>
        <div className="p-4 md:p-6 max-w-[1400px] mx-auto pb-24 md:pb-10">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/chantiers" element={<Chantiers />} />
            <Route path="/materiaux" element={isPatron() ? <Materiaux /> : <Navigate to="/demandes" replace />} />
            <Route path="/calendrier" element={<Calendrier />} />
            <Route path="/rdv" element={isPatron() ? <RDV /> : <Navigate to="/" replace />} />
            <Route path="/demandes" element={<DemandesMateriaux />} />
            <Route path="/equipe" element={isPatron() ? <Equipe /> : <Navigate to="/" replace />} />
            <Route path="/notes" element={isPatron() ? <NotesChantier /> : <Navigate to="/" replace />} />
            <Route path="/visites" element={isPatron() ? <VisitesChantier /> : <Navigate to="/" replace />} />
            <Route path="/visites/:id" element={isPatron() ? <VisiteCanvas /> : <Navigate to="/" replace />} />
            <Route path="/assistant" element={isPatron() ? <Assistant /> : <Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>

      {/* FAB Quick Actions */}
      <QuickActionFAB />

      {/* Mobile Bottom Nav */}
      {isMobile && <MobileNav isPatron={isPatron()} />}
    </div>
  );
}

// Mobile bottom navigation
function MobileNav({ isPatron }) {
  const { t, language, toggleLanguage } = useLanguage();
  const { user, logout } = useAuth();
  const [showMore, setShowMore] = useState(false);
  const navigate = useNavigate();

  const patronMore = [
    { to: '/visites',    icon: ClipboardCheck, label: "Visites",             color: '#0F2744', bg: '#EFF6FF' },
    { to: '/notes',      icon: FileText,  label: t('notesChantierTitle'), color: '#F97316', bg: '#FFF7ED' },
    { to: '/materiaux',  icon: Package,   label: t('materiaux'),  color: '#8B5CF6', bg: '#F5F3FF' },
    { to: '/equipe',     icon: Users,     label: t('equipeTitle'), color: '#10B981', bg: '#ECFDF5' },
    { to: '/calendrier', icon: Calendar,  label: t('calendrier'),  color: '#3B82F6', bg: '#EFF6FF' },
    { to: '/assistant',  icon: Sparkles,  label: t('assistant'),   color: '#64748B', bg: '#F8FAFC' },
  ];

  return (
    <>
      {/* Drawer "Plus" */}
      {showMore && (
        <div
          className="fixed inset-0 z-[9998] bg-black/50"
          onClick={() => setShowMore(false)}
        >
          <div
            className="absolute bottom-[58px] left-0 right-0 bg-white rounded-t-3xl shadow-2xl px-5 pt-5 pb-4"
            onClick={e => e.stopPropagation()}
          >
            {/* Handle */}
            <div className="w-10 h-1 bg-[#CBD5E1] rounded-full mx-auto mb-4" />

            {/* Profile card */}
            <div className="flex items-center gap-3 bg-[#0F2744] rounded-2xl px-4 py-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#3B82F6] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {user?.prenom?.[0]}{user?.nom?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm truncate">{user?.prenom} {user?.nom}</p>
                <p className="text-slate-400 text-xs">{user?.role === 'patron' ? '👔 Patron' : '👷 Ouvrier'}</p>
              </div>
              <button
                onClick={() => { logout(); setShowMore(false); }}
                className="flex items-center gap-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors"
              >
                <LogOut className="w-3.5 h-3.5" />
                {t('logout')}
              </button>
            </div>

            {/* Language switcher */}
            <div className="flex items-center justify-between bg-[#F8FAFC] rounded-2xl px-4 py-3 mb-4 border border-[#E2E8F0]">
              <span className="text-sm font-semibold text-[#475569]">{t('language')}</span>
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1 bg-white border border-[#E2E8F0] rounded-xl px-3 py-1.5 shadow-sm"
              >
                <span className={`text-sm font-bold ${language === 'fr' ? 'text-[#1E293B]' : 'text-[#CBD5E1]'}`}>FR</span>
                <span className="text-[#E2E8F0] text-xs">|</span>
                <span className={`text-sm font-bold ${language === 'ro' ? 'text-[#1E293B]' : 'text-[#CBD5E1]'}`}>RO</span>
              </button>
            </div>

            {/* Grid of actions — 2 columns, big and readable */}
            <div className="grid grid-cols-2 gap-3">
              {patronMore.map(item => (
                <button
                  key={item.to}
                  onClick={() => { navigate(item.to); setShowMore(false); }}
                  className="flex items-center gap-3 p-4 rounded-2xl active:scale-95 transition-transform border border-[#F1F5F9]"
                  style={{ background: item.bg }}
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${item.color}22` }}
                  >
                    <item.icon className="w-5 h-5" style={{ color: item.color }} />
                  </div>
                  <span className="text-[14px] text-[#1E293B] font-semibold">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bottom bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E2E8F0] shadow-[0_-2px_12px_rgba(0,0,0,0.08)] z-[9999]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 4px)' }}
      >
        <div className="flex justify-around items-center px-1 h-14">
          <MobileNavItem to="/"          icon={LayoutDashboard} label={t('home')}     activeColor="#3B82F6" />
          <MobileNavItem to="/chantiers" icon={Building2}       label={t('chantiers')} activeColor="#3B82F6" />
          <MobileNavItem to="/demandes"  icon={ClipboardList}   label={t('demandes')} activeColor="#F97316" />
          {isPatron && <MobileNavItem to="/rdv" icon={CalendarClock} label="RDV" activeColor="#8B5CF6" />}
          {isPatron && (
            <button
              onClick={() => setShowMore(!showMore)}
              className="flex flex-col items-center gap-0.5 px-3 py-1 min-w-[52px]"
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-xl transition-colors ${showMore ? 'bg-orange-100' : ''}`}>
                <MoreHorizontal className={`w-5 h-5 ${showMore ? 'text-[#F97316]' : 'text-[#94A3B8]'}`} />
              </div>
              <span className={`text-[10px] font-medium ${showMore ? 'text-[#F97316]' : 'text-[#94A3B8]'}`}>Plus</span>
            </button>
          )}
        </div>
      </nav>
    </>
  );
}

function MobileNavItem({ to, icon: Icon, label, activeColor = "#3B82F6" }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className="flex flex-col items-center gap-0.5 px-3 py-1 min-w-[52px]"
    >
      {({ isActive }) => (
        <>
          <div
            className="w-8 h-8 flex items-center justify-center rounded-xl transition-all"
            style={isActive ? { background: `${activeColor}18` } : {}}
          >
            <Icon
              className="w-5 h-5 transition-all"
              style={{ color: isActive ? activeColor : '#94A3B8', transform: isActive ? 'scale(1.1)' : 'scale(1)' }}
            />
          </div>
          <span
            className="text-[10px] font-medium"
            style={{ color: isActive ? activeColor : '#94A3B8' }}
          >
            {label}
          </span>
        </>
      )}
    </NavLink>
  );
}

function AppRouter() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#EEF2F7] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#F97316] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return (
    <Routes>
      <Route path="/galerie/:slug" element={<GaleriePublique />} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/*" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <div className="App min-h-screen bg-[#EEF2F7]">
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </div>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
